import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from "@mui/material";
import EmptyState from "../UI/EmptyState";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";

const LeagueTable = ({ onTeamFilter, ...props }) => {
  const matches = props.matches || [];

  const teams = new Map();

  // Collect teams from matches
  matches.forEach((match) => {
    if (match.homeTeam) {
      teams.set(match.homeTeam.id, match.homeTeam);
    }
    if (match.awayTeam) {
      teams.set(match.awayTeam.id, match.awayTeam);
    }
  });

  // Initialize table rows
  const rowsMap = new Map();
  Array.from(teams.values()).forEach((team, idx) => {
    rowsMap.set(team.id, {
      id: idx,
      team: team.name,
      wins: 0,
      draws: 0,
      losses: 0,
      gamesPlayed: 0,
      goalsFor: 0,
      goalsAgainst: 0,
      goalDifference: 0,
      points: 0,
    });
  });

  // Aggregate results from matches that have been played
  matches.forEach((match) => {
    if (!match.hasBeenPlayed) return;

    const homeId = match.homeTeam?.id;
    const awayId = match.awayTeam?.id;
    const homeScore = Number(match.homeScore) || 0;
    const awayScore = Number(match.awayScore) || 0;

    if (!rowsMap.has(homeId) || !rowsMap.has(awayId)) return;

    const homeRow = rowsMap.get(homeId);
    const awayRow = rowsMap.get(awayId);

    homeRow.gamesPlayed += 1;
    awayRow.gamesPlayed += 1;

    homeRow.goalsFor += homeScore;
    homeRow.goalsAgainst += awayScore;
    awayRow.goalsFor += awayScore;
    awayRow.goalsAgainst += homeScore;

    if (homeScore > awayScore) {
      homeRow.wins += 1;
      awayRow.losses += 1;
      homeRow.points += 3;
    } else if (homeScore < awayScore) {
      awayRow.wins += 1;
      homeRow.losses += 1;
      awayRow.points += 3;
    } else {
      homeRow.draws += 1;
      awayRow.draws += 1;
      homeRow.points += 1;
      awayRow.points += 1;
    }
  });

  // Finalize goal difference
  rowsMap.forEach((row) => {
    row.goalDifference = row.goalsFor - row.goalsAgainst;
  });

  // Sort by points, goal difference, goals for, then team name
  const rows = Array.from(rowsMap.values()).sort((a, b) => {
    if (b.points !== a.points) return b.points - a.points;
    if (b.goalDifference !== a.goalDifference)
      return b.goalDifference - a.goalDifference;
    if (b.goalsFor !== a.goalsFor) return b.goalsFor - a.goalsFor;
    return a.team.localeCompare(b.team);
  });

  if (rows.length === 0) {
    return <EmptyState icon={<LeaderboardIcon />} message="No matches played yet." />;
  }

  return (
    <TableContainer
      className="tableContainer"
      data-testid="leagueTable"
      component={Paper}
      sx={{ overflowX: "auto" }}
    >
      <Table sx={{ minWidth: 650 }} aria-label="simple table">
        <TableHead>
          <TableRow>
            <TableCell>Team</TableCell>
            <TableCell>GP</TableCell>
            <TableCell>W</TableCell>
            <TableCell>D</TableCell>
            <TableCell>L</TableCell>
            <TableCell>GF</TableCell>
            <TableCell>GA</TableCell>
            <TableCell>GD</TableCell>
            <TableCell>P</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              hover
              key={row.id}
              sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
            >
              <TableCell>
                {onTeamFilter ? (
                  <Typography
                    component="span"
                    sx={{ cursor: "pointer", color: "primary.main", "&:hover": { textDecoration: "underline" } }}
                    onClick={() => onTeamFilter(row.team)}
                  >
                    {row.team}
                  </Typography>
                ) : (
                  row.team
                )}
              </TableCell>
              <TableCell>{row.gamesPlayed}</TableCell>
              <TableCell>{row.wins}</TableCell>
              <TableCell>{row.draws}</TableCell>
              <TableCell>{row.losses}</TableCell>
              <TableCell>{row.goalsFor}</TableCell>
              <TableCell>{row.goalsAgainst}</TableCell>
              <TableCell>{row.goalDifference}</TableCell>
              <TableCell>{row.points}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default LeagueTable;
